import { assertPartialSchema, createAction, useSelector, v } from "../../../lib";
import { mergeUniqueItems, toHydratedSet } from "../../serializable";

export enum GarminDirectDataDigestActionType {
  GARMIN_DIRECT_BBI_DIGEST_SET = "GARMIN_DIRECT_BBI_DIGEST_SET",
}

export const doGARMIN_DIRECT_BBI_DIGEST_SET = createAction(
  GarminDirectDataDigestActionType.GARMIN_DIRECT_BBI_DIGEST_SET,
);

interface GarminDirectDataDigestState {
  [projectId: string]: SerializableProjectGarminDirectDataDigest;
}

interface SerializableProjectGarminDirectDataDigest {
  coveredYymmddIndexList: number[];
  participantDataDigestMap: ParticipantGarminDataDigestMap;
}

export interface ProjectGarminDirectDataDigest {
  coveredYymmddIndexSet: Set<number>;
  participantDataDigestMap: ParticipantGarminDataDigestMap;
}

export interface ProjectBbiDataDigest {
  projectId: string;
  queryYymmddIndexList: number[];
  participantBbiDataDigestMap: ParticipantGarminDataDigestMap;
}

export type ParticipantGarminDataDigestMap = {
  [participantId: string]: GarminDirectDataDigestDateMap;
};
export type GarminDirectDataDigestDateMap = { [yymmdd: string]: GarminDirectDataDigest | null };
export interface GarminDirectDataDigest {
  wearTime: number;
}

interface RootState {
  garminDirectDataDigest: GarminDirectDataDigestState;
}

const projectGarminDirectDataDigestCache = new WeakMap<
  SerializableProjectGarminDirectDataDigest,
  ProjectGarminDirectDataDigest
>();

/* selector */
export const useSelectGarminDirectDataDigest = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId != undefined) {
      return hydrateProjectGarminDirectDataDigest(state.garminDirectDataDigest[projectId]);
    }
    return undefined;
  });
};

export const useSelectGarminDirectParticipantDataDigestMap = (projectId: string | undefined) => {
  return useSelector((state: RootState) => {
    if (projectId === undefined) {
      return undefined;
    }
    return state.garminDirectDataDigest[projectId]?.participantDataDigestMap;
  });
};

export const useSelectParticipantGarminDirectDataDigest = (
  projectId: string | undefined,
  participantId: string,
  yymmddIndex: string,
) => {
  return useSelector((state: RootState) => {
    if (projectId === undefined) {
      return undefined;
    }
    return state.garminDirectDataDigest[projectId]?.participantDataDigestMap[participantId]?.[
      yymmddIndex
    ];
  });
};

export const useSelectGarminDirectDateDigestCoveredIndexSet = (projectId: string) => {
  return useSelector(
    (state: RootState) =>
      hydrateProjectGarminDirectDataDigest(state.garminDirectDataDigest[projectId])
        ?.coveredYymmddIndexSet,
  );
};

export const garminDirectDataDigestActionCreators = {
  doGARMIN_DIRECT_BBI_DIGEST_SET,
};

export const garminDirectDataDigestDefaultState: GarminDirectDataDigestState = {};

type Action = {
  type: GarminDirectDataDigestActionType.GARMIN_DIRECT_BBI_DIGEST_SET;
  payload: {
    projectBbiDataDigest: ProjectBbiDataDigest;
  };
};

export const garminDirectDataDigestReducer = (
  state = { ...garminDirectDataDigestDefaultState },
  { type, payload }: Action,
): GarminDirectDataDigestState => {
  switch (type) {
    case GarminDirectDataDigestActionType.GARMIN_DIRECT_BBI_DIGEST_SET:
      assertPartialSchema({
        payload,
        schema: v.object({
          projectBbiDataDigest: v.object({
            projectId: v.string().exist(),
            queryYymmddIndexList: v.array().items(v.number()).required(),
            participantBbiDataDigestMap: v.object().exist(),
          }),
        }),
      });

      return updateProjectBbiDataDigestInState(state, payload.projectBbiDataDigest);

    default:
      return state;
  }
};

export const hydrateProjectGarminDirectDataDigest = (
  projectDataDigest: SerializableProjectGarminDirectDataDigest | undefined,
): ProjectGarminDirectDataDigest | undefined => {
  if (!projectDataDigest) {
    return undefined;
  }

  const cachedProjectDataDigest = projectGarminDirectDataDigestCache.get(projectDataDigest);
  if (cachedProjectDataDigest) {
    return cachedProjectDataDigest;
  }

  const hydratedProjectDataDigest: ProjectGarminDirectDataDigest = {
    coveredYymmddIndexSet:
      toHydratedSet(projectDataDigest.coveredYymmddIndexList) ?? new Set<number>(),
    participantDataDigestMap: projectDataDigest.participantDataDigestMap,
  };

  projectGarminDirectDataDigestCache.set(projectDataDigest, hydratedProjectDataDigest);

  return hydratedProjectDataDigest;
};

function updateProjectBbiDataDigestInState(
  state: GarminDirectDataDigestState,
  projectBbiDataDigest: ProjectBbiDataDigest,
) {
  const { projectId, queryYymmddIndexList, participantBbiDataDigestMap } = projectBbiDataDigest;

  const existingProjectDigest = state[projectId] ?? {
    coveredYymmddIndexList: [],
    participantDataDigestMap: {},
  };

  const participantDataDigestMap: ParticipantGarminDataDigestMap = {
    ...existingProjectDigest.participantDataDigestMap,
  };

  for (const [participantId, bbiDataDigestMap] of Object.entries(participantBbiDataDigestMap)) {
    participantDataDigestMap[participantId] = {
      ...(participantDataDigestMap[participantId] ?? {}),
      ...bbiDataDigestMap,
    };
  }

  return {
    ...state,
    [projectId]: {
      coveredYymmddIndexList:
        mergeUniqueItems(existingProjectDigest.coveredYymmddIndexList, queryYymmddIndexList) ?? [],
      participantDataDigestMap,
    },
  };
}
